import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { useUser } from "~/api/users/hooks/use-user";

import { useCollectTillContext } from "../__app.collect/collect-till-context";

import { evaluateBasket, resolveBalanceInclVat } from "./collect-debt";
import { showInsufficientBalanceToast } from "./insufficient-balance-toast";
import {
  type PreOrderLine,
  useCollectPreOrderLines,
  useOrdersCreateActions
} from "./orders-create-context";

/** Returns whether the line was taken, so callers can defer navigation to it. */
export type TakeCandidate = (args: {
  candidate: PreOrderLine;
  productName: string;
}) => boolean;

/**
 * Prices the whole basket once, and owns the only path that may add a line:
 * `takeCandidate` refuses an unfundable one with a toast instead of toggling it.
 */
export function useCollectDebtGate({ childId }: { childId: string | null }) {
  const { locale, currency } = useCurrentTillLocalisation();

  const lines = useCollectPreOrderLines();

  const modules = useCollectTillContext(
    (till) => till.config.collectionModules
  );

  const { data: user } = useUser({ childId });

  const { togglePreOrderLine } = useOrdersCreateActions();

  const { shortfallInclVat, resultingBalanceInclVat, shortfallForCandidate } =
    evaluateBasket(lines, modules, resolveBalanceInclVat(user, currency));

  const takeCandidate: TakeCandidate = ({ candidate, productName }) => {
    const missingInclVat = shortfallForCandidate(candidate);

    if (missingInclVat !== null) {
      showInsufficientBalanceToast({
        shortfallInclVat: missingInclVat,
        productName,
        currency,
        locale
      });

      return false;
    }

    togglePreOrderLine(candidate);

    return true;
  };

  return { shortfallInclVat, resultingBalanceInclVat, takeCandidate };
}
